import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const permisos = user.permisos || [];

  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    return saved === 'true';
  });

  useEffect(() => {
    // Protección de Sesión: Si no hay usuario, volver al login
    if (!localStorage.getItem('user')) {
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', isCollapsed);
  }, [isCollapsed]);

  const [theme, setTheme] = useState(localStorage.getItem('app-theme') || 'dark');

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('app-theme', newTheme);
    document.body.setAttribute('data-theme', newTheme);
  };

  const menuItems = [
    {
      path: '/dashboard',
      label: 'Inventario',
      icon: 'fa-solid fa-laptop-file',
      permiso: 'VER_EQUIPOS'
    },
    {
      path: '/funcionarios',
      label: 'Funcionarios',
      icon: 'fa-solid fa-users-gear',
      permiso: 'VER_FUNCIONARIOS'
    },
    {
      path: '/solicitudes',
      label: 'Solicitudes',
      icon: 'fa-solid fa-clipboard-list',
      permiso: 'VER_SOLICITUDES'
    },
    {
      path: '/supervisor',
      label: 'Análisis KPIs',
      icon: 'fa-solid fa-chart-line',
      permiso: 'VER_ANALISIS'
    },
    {
      path: '/usuarios',
      label: 'Seguridad y Roles',
      icon: 'fa-solid fa-user-shield',
      permiso: 'MODULO_ADMIN'
    },
    {
      path: '/auditoria',
      label: 'Auditoría',
      icon: 'fa-solid fa-clock-rotate-left',
      permiso: 'MODULO_ADMIN'
    }
  ];

  const filteredMenu = menuItems.filter(item => {
    const roleString = user.rol?.toLowerCase() || '';
    // El rol 'superadmin' y 'admin' tienen acceso total por defecto si algo falla con los permisos granulares
    if (roleString === 'superadmin' || roleString === 'admin') return true;
    
    // Si es jefatura y no tiene permisos personalizados, por defecto solo ve solicitudes (esto se manejará ahora por el array de permisos que viene del backend)
    
    return permisos.includes(item.permiso);
  });

  return (
    <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <div className="logo-container">
          <div className="logo-icon"><i className="fa-solid fa-shield-halved"></i></div>
          <div className="logo-text">
            <span className="logo-main">GACTIVOS</span>
            <span className="logo-sub">Gestión Municipal</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button 
            onClick={toggleTheme}
            className="theme-toggle-sidebar"
            title={theme === 'dark' ? "Cambiar a Modo Claro" : "Cambiar a Modo Oscuro"}
          >
            <i className={theme === 'dark' ? 'fa-solid fa-sun' : 'fa-solid fa-moon'}></i>
          </button>
          <button 
            className="sidebar-toggle-btn"
            onClick={() => setIsCollapsed(!isCollapsed)}
            title={isCollapsed ? "Expandir menú" : "Contraer menú"}
          >
            <i className={`fa-solid ${isCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'}`}></i>
          </button>
        </div>
      </div>

      <nav className="sidebar-nav">
        {filteredMenu.map((item) => {
          const isActive = location.pathname === item.path;
          const isUsuariosActive = item.path === '/usuarios' && isActive;
          const isSupervisorActive = item.path === '/supervisor' && isActive;
          const isSolicitudesActive = item.path === '/solicitudes' && isActive;
          const isFuncionariosActive = item.path === '/funcionarios' && isActive;
          const isInventarioActive = item.path === '/dashboard' && isActive;
          const searchParams = new URLSearchParams(location.search);
          const currentTab = searchParams.get('tab') || (isInventarioActive ? 'activo' : isSupervisorActive ? 'inventory' : isSolicitudesActive ? 'pendientes' : isFuncionariosActive ? 'activos' : 'usuarios');
          
          const roleString = user.rol?.toLowerCase() || '';
          const isAdmin = roleString === 'superadmin' || permisos.includes('MODULO_ADMIN');

          return (
            <div key={item.path}>
              <Link
                to={item.path}
                className={`nav-item ${isActive && !isUsuariosActive && !isSupervisorActive && !isSolicitudesActive && !isFuncionariosActive && !isInventarioActive ? 'active' : ''}`}
                style={isActive && (isUsuariosActive || isSupervisorActive || isSolicitudesActive || isFuncionariosActive || isInventarioActive) ? { background: 'var(--sidebar-accent)', color: 'var(--p-indigo)' } : {}}
                title={isCollapsed ? item.label : ''}
              >
                <i className={item.icon}></i>
                <span className="nav-label">{item.label}</span>
                {(item.path === '/usuarios' || item.path === '/supervisor' || item.path === '/solicitudes' || item.path === '/funcionarios' || item.path === '/dashboard') && !isCollapsed && (
                  <i className={`fa-solid fa-chevron-${isActive ? 'down' : 'right'}`} style={{ marginLeft: 'auto', fontSize: '10px', opacity: 0.5 }}></i>
                )}
              </Link>
              
              {isUsuariosActive && !isCollapsed && (
                <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: '40px', marginTop: '4px', gap: '4px', marginBottom: '8px' }} className="animate-fade-in">
                  <Link to="/usuarios?tab=usuarios" style={{ fontSize: '13px', padding: '8px 12px', borderRadius: '8px', color: currentTab === 'usuarios' ? 'var(--p-indigo)' : 'var(--p-slate)', textDecoration: 'none', background: currentTab === 'usuarios' ? 'rgba(99, 102, 241, 0.1)' : 'transparent', fontWeight: currentTab === 'usuarios' ? '700' : '500', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
                    <i className="fa-solid fa-user-group" style={{ fontSize: '11px', width: '16px', textAlign: 'center' }}></i> Usuarios
                  </Link>
                  <Link to="/usuarios?tab=config" style={{ fontSize: '13px', padding: '8px 12px', borderRadius: '8px', color: currentTab === 'config' ? 'var(--p-indigo)' : 'var(--p-slate)', textDecoration: 'none', background: currentTab === 'config' ? 'rgba(99, 102, 241, 0.1)' : 'transparent', fontWeight: currentTab === 'config' ? '700' : '500', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
                    <i className="fa-solid fa-shield-halved" style={{ fontSize: '11px', width: '16px', textAlign: 'center' }}></i> Seguridad
                  </Link>
                  <Link to="/usuarios?tab=bloqueados" style={{ fontSize: '13px', padding: '8px 12px', borderRadius: '8px', color: currentTab === 'bloqueados' ? 'var(--p-indigo)' : 'var(--p-slate)', textDecoration: 'none', background: currentTab === 'bloqueados' ? 'rgba(99, 102, 241, 0.1)' : 'transparent', fontWeight: currentTab === 'bloqueados' ? '700' : '500', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
                    <i className="fa-solid fa-user-lock" style={{ fontSize: '11px', width: '16px', textAlign: 'center' }}></i> Bloqueadas
                  </Link>
                  <Link to="/usuarios?tab=roles" style={{ fontSize: '13px', padding: '8px 12px', borderRadius: '8px', color: currentTab === 'roles' ? 'var(--p-indigo)' : 'var(--p-slate)', textDecoration: 'none', background: currentTab === 'roles' ? 'rgba(99, 102, 241, 0.1)' : 'transparent', fontWeight: currentTab === 'roles' ? '700' : '500', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
                    <i className="fa-solid fa-key" style={{ fontSize: '11px', width: '16px', textAlign: 'center' }}></i> Permisos y Roles
                  </Link>
                </div>
              )}

              {isInventarioActive && !isCollapsed && (
                <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: '40px', marginTop: '4px', gap: '4px', marginBottom: '8px' }} className="animate-fade-in">
                  <Link to="/dashboard?tab=activo" style={{ fontSize: '13px', padding: '8px 12px', borderRadius: '8px', color: currentTab === 'activo' ? 'var(--p-indigo)' : 'var(--p-slate)', textDecoration: 'none', background: currentTab === 'activo' ? 'rgba(99, 102, 241, 0.1)' : 'transparent', fontWeight: currentTab === 'activo' ? '700' : '500', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
                    <i className="fa-solid fa-laptop" style={{ fontSize: '11px', width: '16px', textAlign: 'center' }}></i> Activos
                  </Link>
                  <Link to="/dashboard?tab=periferico" style={{ fontSize: '13px', padding: '8px 12px', borderRadius: '8px', color: currentTab === 'periferico' ? 'var(--p-indigo)' : 'var(--p-slate)', textDecoration: 'none', background: currentTab === 'periferico' ? 'rgba(99, 102, 241, 0.1)' : 'transparent', fontWeight: currentTab === 'periferico' ? '700' : '500', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
                    <i className="fa-solid fa-keyboard" style={{ fontSize: '11px', width: '16px', textAlign: 'center' }}></i> Periféricos
                  </Link>
                  <Link to="/dashboard?tab=entregas" style={{ fontSize: '13px', padding: '8px 12px', borderRadius: '8px', color: currentTab === 'entregas' ? 'var(--p-indigo)' : 'var(--p-slate)', textDecoration: 'none', background: currentTab === 'entregas' ? 'rgba(99, 102, 241, 0.1)' : 'transparent', fontWeight: currentTab === 'entregas' ? '700' : '500', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
                    <i className="fa-solid fa-truck-ramp-box" style={{ fontSize: '11px', width: '16px', textAlign: 'center' }}></i> Entregas
                  </Link>
                </div>
              )}

              {isSupervisorActive && !isCollapsed && (
                <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: '40px', marginTop: '4px', gap: '4px', marginBottom: '8px' }} className="animate-fade-in">
                  <Link to="/supervisor?tab=inventory" style={{ fontSize: '13px', padding: '8px 12px', borderRadius: '8px', color: currentTab === 'inventory' ? 'var(--p-indigo)' : 'var(--p-slate)', textDecoration: 'none', background: currentTab === 'inventory' ? 'rgba(99, 102, 241, 0.1)' : 'transparent', fontWeight: currentTab === 'inventory' ? '700' : '500', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
                    <i className="fa-solid fa-boxes-stacked" style={{ fontSize: '11px', width: '16px', textAlign: 'center' }}></i> Inventario
                  </Link>
                  <Link to="/supervisor?tab=personnel" style={{ fontSize: '13px', padding: '8px 12px', borderRadius: '8px', color: currentTab === 'personnel' ? 'var(--p-indigo)' : 'var(--p-slate)', textDecoration: 'none', background: currentTab === 'personnel' ? 'rgba(99, 102, 241, 0.1)' : 'transparent', fontWeight: currentTab === 'personnel' ? '700' : '500', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
                    <i className="fa-solid fa-users" style={{ fontSize: '11px', width: '16px', textAlign: 'center' }}></i> Personal
                  </Link>
                </div>
              )}

              {isSolicitudesActive && !isCollapsed && (
                <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: '40px', marginTop: '4px', gap: '4px', marginBottom: '8px' }} className="animate-fade-in">
                  <Link to="/solicitudes?tab=pendientes" style={{ fontSize: '13px', padding: '8px 12px', borderRadius: '8px', color: currentTab === 'pendientes' ? 'var(--p-indigo)' : 'var(--p-slate)', textDecoration: 'none', background: currentTab === 'pendientes' ? 'rgba(99, 102, 241, 0.1)' : 'transparent', fontWeight: currentTab === 'pendientes' ? '700' : '500', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
                    <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: '11px', width: '16px', textAlign: 'center' }}></i> Por Procesar
                  </Link>
                  <Link to="/solicitudes?tab=historial" style={{ fontSize: '13px', padding: '8px 12px', borderRadius: '8px', color: currentTab === 'historial' ? 'var(--p-indigo)' : 'var(--p-slate)', textDecoration: 'none', background: currentTab === 'historial' ? 'rgba(99, 102, 241, 0.1)' : 'transparent', fontWeight: currentTab === 'historial' ? '700' : '500', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
                    <i className="fa-solid fa-box-archive" style={{ fontSize: '11px', width: '16px', textAlign: 'center' }}></i> Historial
                  </Link>
                </div>
              )}

              {isFuncionariosActive && !isCollapsed && (
                <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: '40px', marginTop: '4px', gap: '4px', marginBottom: '8px' }} className="animate-fade-in">
                  <Link to="/funcionarios?tab=activos" style={{ fontSize: '13px', padding: '8px 12px', borderRadius: '8px', color: currentTab === 'activos' ? 'var(--p-indigo)' : 'var(--p-slate)', textDecoration: 'none', background: currentTab === 'activos' ? 'rgba(99, 102, 241, 0.1)' : 'transparent', fontWeight: currentTab === 'activos' ? '700' : '500', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
                    <i className="fa-solid fa-users" style={{ fontSize: '11px', width: '16px', textAlign: 'center' }}></i> Directorio Oficial
                  </Link>
                  {isAdmin && (
                    <>
                      <Link to="/funcionarios?tab=pendientes" style={{ fontSize: '13px', padding: '8px 12px', borderRadius: '8px', color: currentTab === 'pendientes' ? 'var(--p-indigo)' : 'var(--p-slate)', textDecoration: 'none', background: currentTab === 'pendientes' ? 'rgba(99, 102, 241, 0.1)' : 'transparent', fontWeight: currentTab === 'pendientes' ? '700' : '500', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
                        <i className="fa-solid fa-user-clock" style={{ fontSize: '11px', width: '16px', textAlign: 'center' }}></i> Por Aprobar
                      </Link>
                      <Link to="/funcionarios?tab=desvinculados" style={{ fontSize: '13px', padding: '8px 12px', borderRadius: '8px', color: currentTab === 'desvinculados' ? 'var(--p-indigo)' : 'var(--p-slate)', textDecoration: 'none', background: currentTab === 'desvinculados' ? 'rgba(99, 102, 241, 0.1)' : 'transparent', fontWeight: currentTab === 'desvinculados' ? '700' : '500', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
                        <i className="fa-solid fa-user-slash" style={{ fontSize: '11px', width: '16px', textAlign: 'center' }}></i> Desvinculados
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {/* Mobile Logout Button (Visible only when sidebar-footer is hidden by CSS) */}
        <div 
          className="nav-item mobile-logout" 
          onClick={async () => {
            try {
              const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
              const apiBase = isLocal ? `http://${window.location.hostname}:3010` : `${window.location.protocol}//${window.location.hostname}`;
              await fetch(`${apiBase}/api/logout`, { method: 'POST', credentials: 'include' });
            } catch(e) {}
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            navigate('/');
          }}
          style={{ cursor: 'pointer' }}
          title="Cerrar Sesión"
        >
          <i className="fa-solid fa-right-from-bracket" style={{ color: 'var(--p-rose)' }}></i>
          <span className="nav-label" style={{ color: 'var(--p-rose)' }}>Salir</span>
        </div>
      </nav>

      <div className="sidebar-footer">

          <button
          className="btn-logout-premium"
          onClick={async () => {
            try {
              const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
              const apiBase = isLocal ? `http://${window.location.hostname}:3010` : `${window.location.protocol}//${window.location.hostname}`;
              await fetch(`${apiBase}/api/logout`, { method: 'POST', credentials: 'include' });
            } catch(e) {}
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            navigate('/');
          }}
          style={{
            width: '100%',
            background: 'var(--bg-subtle)',
            color: 'var(--p-rose)',
            border: '2px solid var(--glass-border)',
            padding: '14px',
            borderRadius: '16px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            fontSize: '14px',
            fontWeight: '800',
            marginTop: 'auto',
            minWidth: '0',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 4px 12px rgba(244, 63, 94, 0.05)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--p-rose)';
            e.currentTarget.style.color = 'white';
            e.currentTarget.style.borderColor = 'var(--p-rose)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'var(--bg-subtle)';
            e.currentTarget.style.color = 'var(--p-rose)';
            e.currentTarget.style.borderColor = 'var(--glass-border)';
          }}
          title={isCollapsed ? "Cerrar Sesión" : ""}
        >
          <i className="fa-solid fa-right-from-bracket"></i>
          <span className="logout-text">Cerrar Sesión</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
